import { SetState } from '@/features/remotion-editor/editor/context-provider';
import {
  CaptionAsset,
  isCaptionAsset,
} from '@/features/remotion-editor/editor/assets/assets';
import {
  CaptionsItem,
  isCaptionsItem,
} from '@/features/remotion-editor/editor/items/captions/captions-item-type';
import { generateRandomId } from '@/features/remotion-editor/editor/utils/generate-random-id';
import {
  safeMs,
  cloneCaptions,
  sortCaptionsByStart,
  getCaptionRange,
  createCaptionClipId,
  updateTrackItems,
} from './helpers';
import { calculateCaptionAssetSize } from './asset-utils';
import { withoutCaptionTranslation } from '@/features/remotion-editor/editor/state/actions/caption-translation-actions';

/**
 * Link 활성화 시 단어 삭제를 위해 split 후 해당 단어만 포함된 아이템을 분리합니다.
 * 분리된 아이템 ID를 반환하여 이후 deleteItems로 삭제할 수 있게 합니다.
 *
 * @param itemId 대상 captions item id
 * @param wordIndex 삭제할 단어 index
 * @param trackIndex captions track index
 * @param itemIndex track 내 item index
 * @param setState 전역 상태 업데이트 함수
 * @returns 삭제해야 할 아이템 ID들 (caption + 연결된 video)
 */
export const isolateWordForDeletion = ({
  itemId,
  wordIndex,
  trackIndex,
  itemIndex,
  setState,
}: {
  itemId: string;
  wordIndex: number;
  trackIndex: number;
  itemIndex: number;
  setState: SetState;
}): {
  itemIdToDelete: string | null;
  /** 삭제할 구간의 프레임 범위 (Video split용) */
  deleteFrameRange: { from: number; durationInFrames: number } | null;
} => {
  let result = {
    itemIdToDelete: null as string | null,
    deleteFrameRange: null as { from: number; durationInFrames: number } | null,
  };

  setState({
    update: (prev) => {
      const { tracks, items, assets, fps } = prev.undoableState;
      const targetTrack = tracks[trackIndex];
      const currentItem = items[itemId];

      if (!targetTrack || !currentItem || !isCaptionsItem(currentItem)) {
        return prev;
      }

      const baseAsset = assets[currentItem.assetId];
      if (!isCaptionAsset(baseAsset)) {
        return prev;
      }

      const orderedCaptions = sortCaptionsByStart(
        cloneCaptions(baseAsset.captions),
      );

      const totalWords = orderedCaptions.length;
      const safeFps = fps > 0 ? fps : 30;
      const originalFrom = currentItem.from;
      const originalDuration = Math.max(1, currentItem.durationInFrames);
      const originalEndFrame = originalFrom + originalDuration;
      const baseVideoStart = safeMs(baseAsset.videoStart);

      // 단어가 1개뿐이면 현재 아이템 자체를 삭제 대상으로
      if (totalWords <= 1) {
        result = {
          itemIdToDelete: itemId,
          deleteFrameRange: {
            from: originalFrom,
            durationInFrames: originalDuration,
          },
        };
        return prev;
      }

      // Case 1: 첫 번째 단어 삭제 (wordIndex === 0)
      if (wordIndex === 0) {
        // split(1): [단어0] / [단어1..end]
        const prefixCaptions = orderedCaptions.slice(0, 1);
        const suffixCaptions = orderedCaptions.slice(1);

        const prefixRange = getCaptionRange(prefixCaptions);
        const suffixRange = getCaptionRange(suffixCaptions);

        const suffixOffsetMs = Math.max(0, suffixRange.start - baseVideoStart);
        const suffixOffsetFrames = Math.round(
          (suffixOffsetMs / 1000) * safeFps,
        );
        const desiredSuffixFrom = originalFrom + suffixOffsetFrames;
        const safeSuffixFrom = Math.min(
          Math.max(desiredSuffixFrom, originalFrom + 1),
          originalEndFrame - 1,
        );

        const prefixDurationFrames = Math.max(1, safeSuffixFrom - originalFrom);
        const suffixDurationFrames = Math.max(
          1,
          originalEndFrame - safeSuffixFrom,
        );

        // 원본 아이템은 첫 단어만 포함 (삭제 대상)
        const prefixAsset: CaptionAsset = {
          ...withoutCaptionTranslation(baseAsset),
          captions: prefixCaptions,
          size: calculateCaptionAssetSize(prefixCaptions),
          videoStart: baseVideoStart,
          videoEnd: Math.max(baseVideoStart, prefixRange.end),
        };

        // 새 아이템은 나머지 단어들
        const suffixAssetId = `caption-c${generateRandomId()}`;
        const suffixAsset: CaptionAsset = {
          ...withoutCaptionTranslation(baseAsset),
          id: suffixAssetId,
          captions: suffixCaptions,
          size: calculateCaptionAssetSize(suffixCaptions),
          clipId: createCaptionClipId(),
          filename: `${suffixAssetId}.json`,
          remoteUrl: null,
          remoteFileKey: null,
          videoStart: suffixRange.start,
          videoEnd: Math.max(suffixRange.start, suffixRange.end),
        };

        const updatedCurrentItem: CaptionsItem = {
          ...currentItem,
          from: originalFrom,
          durationInFrames: prefixDurationFrames,
          captionStartInSeconds: safeMs(prefixAsset.videoStart) / 1000,
        };

        const newItemId = `caption-c${generateRandomId()}`;
        const newItem: CaptionsItem = {
          ...currentItem,
          id: newItemId,
          assetId: suffixAsset.id,
          from: safeSuffixFrom,
          durationInFrames: suffixDurationFrames,
          captionStartInSeconds: safeMs(suffixAsset.videoStart) / 1000,
        };

        const updatedTrackItems = updateTrackItems(
          tracks,
          trackIndex,
          (existing) => {
            const next = [...existing];
            next.splice(itemIndex + 1, 0, newItemId);
            return next;
          },
        );

        // 원본 아이템(첫 단어) 삭제
        result = {
          itemIdToDelete: itemId,
          deleteFrameRange: {
            from: originalFrom,
            durationInFrames: prefixDurationFrames,
          },
        };

        return {
          ...prev,
          undoableState: {
            ...prev.undoableState,
            tracks: updatedTrackItems,
            items: {
              ...prev.undoableState.items,
              [currentItem.id]: updatedCurrentItem,
              [newItemId]: newItem,
            },
            assets: {
              ...prev.undoableState.assets,
              [prefixAsset.id]: prefixAsset,
              [suffixAsset.id]: suffixAsset,
            },
          },
        };
      }

      // Case 2: 마지막 단어 삭제 (wordIndex === totalWords - 1)
      if (wordIndex === totalWords - 1) {
        // split(wordIndex): [단어0..wordIndex-1] / [wordIndex]
        const prefixCaptions = orderedCaptions.slice(0, wordIndex);
        const suffixCaptions = orderedCaptions.slice(wordIndex);

        const prefixRange = getCaptionRange(prefixCaptions);
        const suffixRange = getCaptionRange(suffixCaptions);

        const suffixOffsetMs = Math.max(0, suffixRange.start - baseVideoStart);
        const suffixOffsetFrames = Math.round(
          (suffixOffsetMs / 1000) * safeFps,
        );
        const desiredSuffixFrom = originalFrom + suffixOffsetFrames;
        const safeSuffixFrom = Math.min(
          Math.max(desiredSuffixFrom, originalFrom + 1),
          originalEndFrame - 1,
        );

        const prefixDurationFrames = Math.max(1, safeSuffixFrom - originalFrom);
        const suffixDurationFrames = Math.max(
          1,
          originalEndFrame - safeSuffixFrom,
        );

        const prefixAsset: CaptionAsset = {
          ...withoutCaptionTranslation(baseAsset),
          captions: prefixCaptions,
          size: calculateCaptionAssetSize(prefixCaptions),
          videoStart: baseVideoStart,
          videoEnd: Math.max(baseVideoStart, prefixRange.end),
        };

        const suffixAssetId = `caption-c${generateRandomId()}`;
        const suffixAsset: CaptionAsset = {
          ...withoutCaptionTranslation(baseAsset),
          id: suffixAssetId,
          captions: suffixCaptions,
          size: calculateCaptionAssetSize(suffixCaptions),
          clipId: createCaptionClipId(),
          filename: `${suffixAssetId}.json`,
          remoteUrl: null,
          remoteFileKey: null,
          videoStart: suffixRange.start,
          videoEnd: Math.max(suffixRange.start, suffixRange.end),
        };

        const updatedCurrentItem: CaptionsItem = {
          ...currentItem,
          from: originalFrom,
          durationInFrames: prefixDurationFrames,
          captionStartInSeconds: safeMs(prefixAsset.videoStart) / 1000,
        };

        const newItemId = `caption-c${generateRandomId()}`;
        const newItem: CaptionsItem = {
          ...currentItem,
          id: newItemId,
          assetId: suffixAsset.id,
          from: safeSuffixFrom,
          durationInFrames: suffixDurationFrames,
          captionStartInSeconds: safeMs(suffixAsset.videoStart) / 1000,
        };

        const updatedTrackItems = updateTrackItems(
          tracks,
          trackIndex,
          (existing) => {
            const next = [...existing];
            next.splice(itemIndex + 1, 0, newItemId);
            return next;
          },
        );

        // 새 아이템(마지막 단어) 삭제
        result = {
          itemIdToDelete: newItemId,
          deleteFrameRange: {
            from: safeSuffixFrom,
            durationInFrames: suffixDurationFrames,
          },
        };

        return {
          ...prev,
          undoableState: {
            ...prev.undoableState,
            tracks: updatedTrackItems,
            items: {
              ...prev.undoableState.items,
              [currentItem.id]: updatedCurrentItem,
              [newItemId]: newItem,
            },
            assets: {
              ...prev.undoableState.assets,
              [prefixAsset.id]: prefixAsset,
              [suffixAsset.id]: suffixAsset,
            },
          },
        };
      }

      // Case 3: 중간 단어 삭제
      // 두 번 split: [0..wordIndex-1] / [wordIndex] / [wordIndex+1..end]
      const prefixCaptions = orderedCaptions.slice(0, wordIndex);
      const middleCaptions = orderedCaptions.slice(wordIndex, wordIndex + 1);
      const suffixCaptions = orderedCaptions.slice(wordIndex + 1);

      const prefixRange = getCaptionRange(prefixCaptions);
      const middleRange = getCaptionRange(middleCaptions);
      const suffixRange = getCaptionRange(suffixCaptions);

      // 타임라인 프레임 계산
      const middleOffsetMs = Math.max(0, middleRange.start - baseVideoStart);
      const suffixOffsetMs = Math.max(0, suffixRange.start - baseVideoStart);

      const middleOffsetFrames = Math.round((middleOffsetMs / 1000) * safeFps);
      const suffixOffsetFrames = Math.round((suffixOffsetMs / 1000) * safeFps);

      const middleFrom = Math.min(
        Math.max(originalFrom + middleOffsetFrames, originalFrom + 1),
        originalEndFrame - 2,
      );
      const suffixFrom = Math.min(
        Math.max(originalFrom + suffixOffsetFrames, middleFrom + 1),
        originalEndFrame - 1,
      );

      const prefixDurationFrames = Math.max(1, middleFrom - originalFrom);
      const middleDurationFrames = Math.max(1, suffixFrom - middleFrom);
      const suffixDurationFrames = Math.max(1, originalEndFrame - suffixFrom);

      // Prefix asset (기존 아이템 유지)
      const prefixAsset: CaptionAsset = {
        ...withoutCaptionTranslation(baseAsset),
        captions: prefixCaptions,
        size: calculateCaptionAssetSize(prefixCaptions),
        videoStart: baseVideoStart,
        videoEnd: Math.max(baseVideoStart, prefixRange.end),
      };

      // Middle asset (삭제 대상)
      const middleAssetId = `caption-c${generateRandomId()}`;
      const middleAsset: CaptionAsset = {
        ...withoutCaptionTranslation(baseAsset),
        id: middleAssetId,
        captions: middleCaptions,
        size: calculateCaptionAssetSize(middleCaptions),
        clipId: createCaptionClipId(),
        filename: `${middleAssetId}.json`,
        remoteUrl: null,
        remoteFileKey: null,
        videoStart: middleRange.start,
        videoEnd: Math.max(middleRange.start, middleRange.end),
      };

      // Suffix asset
      const suffixAssetId = `caption-c${generateRandomId()}`;
      const suffixAsset: CaptionAsset = {
        ...withoutCaptionTranslation(baseAsset),
        id: suffixAssetId,
        captions: suffixCaptions,
        size: calculateCaptionAssetSize(suffixCaptions),
        clipId: createCaptionClipId(),
        filename: `${suffixAssetId}.json`,
        remoteUrl: null,
        remoteFileKey: null,
        videoStart: suffixRange.start,
        videoEnd: Math.max(suffixRange.start, suffixRange.end),
      };

      const updatedCurrentItem: CaptionsItem = {
        ...currentItem,
        from: originalFrom,
        durationInFrames: prefixDurationFrames,
        captionStartInSeconds: safeMs(prefixAsset.videoStart) / 1000,
      };

      const middleItemId = `caption-c${generateRandomId()}`;
      const middleItem: CaptionsItem = {
        ...currentItem,
        id: middleItemId,
        assetId: middleAsset.id,
        from: middleFrom,
        durationInFrames: middleDurationFrames,
        captionStartInSeconds: safeMs(middleAsset.videoStart) / 1000,
      };

      const suffixItemId = `caption-c${generateRandomId()}`;
      const suffixItem: CaptionsItem = {
        ...currentItem,
        id: suffixItemId,
        assetId: suffixAsset.id,
        from: suffixFrom,
        durationInFrames: suffixDurationFrames,
        captionStartInSeconds: safeMs(suffixAsset.videoStart) / 1000,
      };

      const updatedTrackItems = updateTrackItems(
        tracks,
        trackIndex,
        (existing) => {
          const next = [...existing];
          // 현재 위치 뒤에 middle, suffix 순서로 삽입
          next.splice(itemIndex + 1, 0, middleItemId, suffixItemId);
          return next;
        },
      );

      // 중간 아이템 삭제
      result = {
        itemIdToDelete: middleItemId,
        deleteFrameRange: {
          from: middleFrom,
          durationInFrames: middleDurationFrames,
        },
      };

      return {
        ...prev,
        undoableState: {
          ...prev.undoableState,
          tracks: updatedTrackItems,
          items: {
            ...prev.undoableState.items,
            [currentItem.id]: updatedCurrentItem,
            [middleItemId]: middleItem,
            [suffixItemId]: suffixItem,
          },
          assets: {
            ...prev.undoableState.assets,
            [prefixAsset.id]: prefixAsset,
            [middleAsset.id]: middleAsset,
            [suffixAsset.id]: suffixAsset,
          },
        },
      };
    },
    commitToUndoStack: false, // 삭제와 함께 하나의 undo로 처리
  });

  return result;
};
